import { inject, Injectable } from '@angular/core';
import { SqliteService } from '@app/services';
import { UserRepository } from './user.repository';
import { CreatePaymentModel, PaymentModel, RouteModel, StoreModel } from '@app/models';
import { PaymentStatusEnum } from '@app/enums';

@Injectable({
    providedIn: 'root',
})
export class PaymentRepository
{
    private readonly keys = {
        tableName: 'payments',
    };
    private sqliteService = inject(SqliteService);
    private userRepository = inject(UserRepository);

    async updatePayment(id: number, updatePayment: CreatePaymentModel): Promise<PaymentModel>
    {
        const columns = [
            'payment_method_id',
            'payment_reference',
            'preordered_total',
            'order_total',
            'returns_total',
            'amount_paid',
            'given_crates',
            'returned_crates',
            'foreign_returned_crates',
            'status',
        ];
        const setClause = columns.map((col) => `${col} = ?`).join(', ');
        const sql = `
            UPDATE ${this.keys.tableName}
            SET ${setClause}
            WHERE id = ?;
        `;
        await this.sqliteService.save(sql, [
            updatePayment.payment_method_id,
            updatePayment.payment_reference,
            updatePayment.preordered_total,
            updatePayment.order_total,
            updatePayment.returns_total,
            updatePayment.amount_paid,
            updatePayment.given_crates,
            updatePayment.returned_crates,
            updatePayment.foreign_returned_crates,
            updatePayment.status,
            id,
        ]);

        const payment = await this.getPaymentById(id);

        return payment!;
    }

    async createPayment(newPayment: CreatePaymentModel): Promise<PaymentModel | null>
    {
        const user = await this.userRepository.getLoggedInUser();

        if (!user)
        {
            return null;
        }

        const columns = [
            'user_id',
            'check_in_id',
            'store_id',
            'route_id',
            'payment_method_id',
            'payment_reference',
            'preordered_total',
            'order_total',
            'returns_total',
            'amount_paid',
            'given_crates',
            'returned_crates',
            'foreign_returned_crates',
            'status',
        ];
        const valuePlaceholders = this.sqliteService.getValuePlaceholder(1, columns.length);
        const sql = `
            INSERT
            OR REPLACE INTO
            ${this.keys.tableName}
            (
            ${columns.join(', ')}
            )
            VALUES
            ${valuePlaceholders};
        `;
        const id = await this.sqliteService.save(sql, [
            user.id,
            newPayment.check_in_id,
            newPayment.store_id,
            newPayment.route_id,
            newPayment.payment_method_id,
            newPayment.payment_reference,
            newPayment.preordered_total,
            newPayment.order_total,
            newPayment.returns_total,
            newPayment.amount_paid,
            newPayment.given_crates,
            newPayment.returned_crates,
            newPayment.foreign_returned_crates,
            PaymentStatusEnum.DRAFT,
        ]);

        if (id === null)
        {
            return null;
        }

        return await this.getPaymentById(id);
    }

    async getPaymentById(id: number): Promise<PaymentModel | null>
    {
        const sql = `
            SELECT *
            FROM ${this.keys.tableName}
            WHERE id = ? LIMIT 1;
        `;
        const data = await this.sqliteService.get(sql, [id]);

        if (data === null)
        {
            return null;
        }

        return data[0] as PaymentModel;
    }

    async isPaymentCompleted(checkInId: number, storeId: number, routeId: number): Promise<boolean>
    {
        const user = await this.userRepository.getLoggedInUser();

        if (!user)
        {
            return false;
        }

        const payment = await this.getPaymentByCheckIn(checkInId, storeId, routeId);

        if (!payment)
        {
            return false;
        }

        return payment.status === PaymentStatusEnum.COMPLETED;
    }

    async getShortPaidAmount(checkInId: number, store: StoreModel, route: RouteModel): Promise<number | null>
    {
        const payment = await this.getPaymentByCheckIn(checkInId, store.id, route.id);

        if (payment === null || payment.status !== PaymentStatusEnum.COMPLETED || payment.amount_paid === null)
        {
            return null;
        }

        const isPreordered = payment.preordered_total !== null;
        const orderTotal = isPreordered ? parseFloat(payment.preordered_total ?? '0') : parseFloat(payment.order_total ?? '0');

        const amountPaid = parseFloat(payment.amount_paid ?? '0');
        const returnTotal = parseFloat(payment.returns_total ?? '0');
        const amountOwed = orderTotal - returnTotal;

        const shortPaid = amountOwed - amountPaid;

        // Use tolerance for floating-point comparison
        const tolerance = 0.0005;

        return shortPaid > tolerance ? shortPaid : null;
    }

    async getOverPaidAmount(checkInId: number, store: StoreModel, route: RouteModel): Promise<number | null>
    {
        const payment = await this.getPaymentByCheckIn(checkInId, store.id, route.id);

        if (payment === null || payment.status !== PaymentStatusEnum.COMPLETED || payment.amount_paid === null)
        {
            return null;
        }

        const isPreordered = payment.preordered_total !== null;
        const orderTotal = isPreordered ? parseFloat(payment.preordered_total ?? '0') : parseFloat(payment.order_total ?? '0');

        const amountPaid = parseFloat(payment.amount_paid ?? '0');
        const returnTotal = parseFloat(payment.returns_total ?? '0');
        const amountOwed = orderTotal - returnTotal;

        const overPaid = amountPaid - amountOwed;

        // Use tolerance for floating-point comparison
        const tolerance = 0.0005;

        return overPaid > tolerance ? overPaid : null;
    }

    async getPaymentByCheckIn(checkInId: number, storeId: number, routeId: number): Promise<PaymentModel | null>
    {
        const user = await this.userRepository.getLoggedInUser();

        if (!user)
        {
            return null;
        }

        const sql = `
            SELECT *
            FROM ${this.keys.tableName}
            WHERE user_id = ?
              AND check_in_id = ?
              AND store_id = ?
              AND route_id = ? LIMIT 1;
        `;
        const data = await this.sqliteService.get(sql, [
            user.id,
            checkInId,
            storeId,
            routeId,
        ]);

        if (data === null)
        {
            return null;
        }

        return data[0] as PaymentModel;
    }

    async clearUserRoutePayments(routeId: number): Promise<void>
    {
        const user = await this.userRepository.getLoggedInUser();

        if (!user)
        {
            return;
        }

        const sql = `
            DELETE
            FROM ${this.keys.tableName}
            WHERE route_id = ?
              AND user_id = ?;
        `;
        await this.sqliteService.delete(sql, [routeId, user.id]);
    }

    async clearPayment(checkInId: number, storeId: number, routeId: number): Promise<void>
    {
        const user = await this.userRepository.getLoggedInUser();

        if (!user)
        {
            return;
        }

        const sql = `
            DELETE
            FROM ${this.keys.tableName}
            WHERE check_in_id = ?
              AND store_id = ?
              AND route_id = ?
              AND user_id = ?;
        `;
        await this.sqliteService.delete(sql, [checkInId, storeId, routeId, user.id]);
    }
}
